import {
  ALERT_DELIVERY_CHOICES, ALERT_DELIVERY_LABELS, CONTACT_NO_AUTOMATIC_EMAIL, DEFAULT_ALERT_DELIVERY, FACILITIES_CONTACT_ROLES, FACILITIES_CONTACT_ROLE_LABELS,
  isAlertDelivery, isEviAddress, isFacilitiesContactRole, normalizeFacilitiesContacts,
  type AlertDelivery, type FacilitiesContactRole,
} from '@evi/shared';

export { CONTACT_NO_AUTOMATIC_EMAIL };

/**
 * The facilities contacts card's draft rules (Warren, 28 Oct 2026, item 6).
 * The API is the authority (PUT /locations/:id/facilities-contacts runs the
 * same @evi/shared normalizeFacilitiesContacts); this keeps Save from
 * offering a write it would refuse, and says which row to fix.
 *
 * Every contact has a role (Warren, 28 Oct 2026: "Property manager,
 * Concierge, Facilities, Owner, Other"). The API keeps a contact's current
 * role when none is sent; the card always sends one, and a new row must
 * have one chosen before Save.
 */

export interface ContactDraft {
  name: string;
  email: string;
  /** '' until the person picks one on a new row. */
  role: FacilitiesContactRole | '';
  /** How often an EVI person listed here hears about outages; Off on a new row (Warren, 4 Nov 2026). The form offers it
   *  only where deliveryControl says 'choice', and sends it only there. */
  alert_delivery: AlertDelivery;
}

export const ROLE_OPTIONS: ReadonlyArray<{ value: FacilitiesContactRole; label: string }> =
  FACILITIES_CONTACT_ROLES.map((value) => ({ value, label: FACILITIES_CONTACT_ROLE_LABELS[value] }));

export const DELIVERY_OPTIONS: ReadonlyArray<{ value: AlertDelivery; label: string }> =
  ALERT_DELIVERY_CHOICES.map((value) => ({ value, label: ALERT_DELIVERY_LABELS[value] }));
export const NEW_ROW_DELIVERY: AlertDelivery = DEFAULT_ALERT_DELIVERY;

/**
 * What a form row offers for its frequency (Warren, 4 Nov 2026, ruling R7). The viewer is the Api's own flag
 * (viewer_is_evi_staff); the address rule is the Api's own, @evi/shared isEviAddress, so a row still being typed answers
 * as its saved row will.
 *   choice  an EVI staff viewer on an EVI address
 *   note    an address outside EVI, or none yet: the read-only line, for every viewer
 *   none    an EVI address that this viewer may not set
 */
export function deliveryControl(email: string, viewerIsEvi: boolean): 'choice' | 'note' | 'none' {
  if (!isEviAddress(email)) return 'note';
  return viewerIsEvi ? 'choice' : 'none';
}

export function deliveryLabel(delivery: string | null | undefined): string {
  return isAlertDelivery(delivery) ? ALERT_DELIVERY_LABELS[delivery] : ALERT_DELIVERY_LABELS[DEFAULT_ALERT_DELIVERY];
}

export function roleLabel(role: string): string {
  return isFacilitiesContactRole(role) ? FACILITIES_CONTACT_ROLE_LABELS[role] : FACILITIES_CONTACT_ROLE_LABELS.other;
}

/** Rows the person left entirely blank are dropped, not refused. */
export function draftToPayload(rows: ReadonlyArray<ContactDraft>): ContactDraft[] {
  return rows
    .map((r) => ({ name: r.name.trim(), email: r.email.trim(), role: r.role, alert_delivery: r.alert_delivery }))
    .filter((r) => r.name || r.email);
}

export type DraftCheck =
  | { ok: true; contacts: Array<{ name: string; email: string; role: FacilitiesContactRole; alert_delivery?: AlertDelivery }> }
  | { ok: false; error: string };

/** A frequency goes only with a row that offered one: the Api keeps each other contact's own (it ignores one sent). */
export function checkDraft(rows: ReadonlyArray<ContactDraft>, opts: { viewerIsEvi?: boolean } = {}): DraftCheck {
  const contacts = draftToPayload(rows);
  const missing = contacts.findIndex((c) => !c.role);
  if (missing >= 0) return { ok: false, error: `Contact ${missing + 1}: choose a role.` };
  const result = normalizeFacilitiesContacts(contacts, { actorId: null, now: new Date() });
  if (!result.ok) return { ok: false, error: result.error };
  return { ok: true, contacts: result.contacts.map((c) => ({
    name: c.name, email: c.email, role: c.role,
    ...(deliveryControl(c.email, opts.viewerIsEvi === true) === 'choice' ? { alert_delivery: c.alert_delivery } : {}),
  })) };
}

/** Plain words for where a contact came from. */
export function sourceLabel(source: string): string {
  if (source === 'admin') return 'Added in Admin';
  if (source.startsWith('invite:')) return 'Accepted an invite';
  if (source.startsWith('seed:') || source === 'seed') return 'From EV Initiative records';
  if (source.startsWith('import:') || source === 'import') return 'Imported by EV Initiative';
  return 'Added by EV Initiative';
}

/** The anchor and route an email links to: /locations/:id/facilities. */
export const FACILITIES_ANCHOR = 'facilities';
export function facilitiesPath(locationId: string): string {
  return `/locations/${locationId}/${FACILITIES_ANCHOR}`;
}
